/**
 * useRecentActivity — Zustand store backed by localStorage.
 *
 * Tracks the last 20 terminal commands and last 10 tickers viewed,
 * persisted across page reloads via zustand/persist.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

const MAX_SEARCHES = 20
const MAX_TICKERS  = 10

interface RecentActivityState {
  /** Last 20 terminal commands, most recent first */
  recentSearches: string[]
  /** Last 10 tickers viewed, most recent first */
  recentTickers: string[]

  /** Call from Terminal after a command is submitted */
  addSearch: (cmd: string) => void
  /** Call from StockDetailPage / ChartPage when a ticker is loaded */
  addTicker: (ticker: string) => void
  /** Remove a specific ticker from the recently-viewed list */
  removeTicker: (ticker: string) => void
  /** Clear all recent activity */
  clearAll: () => void
}

export const useRecentActivity = create<RecentActivityState>()(
  persist(
    (set) => ({
      recentSearches: [],
      recentTickers:  [],

      addSearch: (cmd) => {
        const trimmed = cmd.trim()
        if (!trimmed) return
        set((s) => ({
          recentSearches: [
            trimmed,
            ...s.recentSearches.filter(c => c !== trimmed),
          ].slice(0, MAX_SEARCHES),
        }))
      },

      addTicker: (ticker) => {
        const t = ticker.toUpperCase().trim()
        if (!t) return
        set((s) => ({
          recentTickers: [
            t,
            ...s.recentTickers.filter(x => x !== t),
          ].slice(0, MAX_TICKERS),
        }))
      },

      removeTicker: (ticker) => {
        const t = ticker.toUpperCase().trim()
        set((s) => ({
          recentTickers: s.recentTickers.filter(x => x !== t),
        }))
      },

      clearAll: () => set({ recentSearches: [], recentTickers: [] }),
    }),
    {
      name: 'av-recent-activity',
    },
  ),
)
